import { localDatabaseUrls } from "../backend/constants";
import { getAPI, putAPI } from "../backend/commons";
import { getJWTToken } from "../shared/fetchWrapper/fetchWrapper";
import AlertManager from "../shared/Alert/Alert";

const RULE_ENDPOINTS = {
  landing: {
    logs: localDatabaseUrls.serviceURL.landingRuleLogs,
    update: localDatabaseUrls.serviceURL.ruleUpdate,
  },
  prioritization: {
    active: localDatabaseUrls.serviceURL.prioritizationRuleActive,
    logs: localDatabaseUrls.serviceURL.prioritizationRuleLogs,
    update: localDatabaseUrls.serviceURL.prioritizationRuleUpdate,
    appendRuleType: false,
  },
  margin: {
    logs: localDatabaseUrls.serviceURL.marginStrategySkuStrategies,
    update: localDatabaseUrls.serviceURL.marginStrategyUpdate,
    appendRuleType: false,
  },
};

const ACTIVE_RULE_ENDPOINT = localDatabaseUrls.serviceURL.activeRuleFlag || null;

const getRuleEndpoint = (ruleType, endpointKey) => RULE_ENDPOINTS[ruleType]?.[endpointKey] || null;
const formatRuleType = (ruleType) =>
  typeof ruleType === "string" && ruleType.length
    ? ruleType.charAt(0).toUpperCase() + ruleType.slice(1)
    : "";

const extractMarginRuleRecords = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.content)) {
    return payload.content;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.data?.content)) {
    return payload.data.content;
  }

  if (Array.isArray(payload?.rows)) {
    return payload.rows;
  }

  return [];
};

const normalizeMarginRulePage = (payload) => ({
  rows: extractMarginRuleRecords(payload),
  totalPages: Number(payload?.totalPages ?? payload?.data?.totalPages ?? 0),
  totalElements: Number(payload?.totalElements ?? payload?.data?.totalElements ?? 0),
  currentPage: Number(payload?.currentPage ?? payload?.page ?? payload?.data?.currentPage ?? payload?.data?.page ?? 0),
  pageSize: Number(payload?.size ?? payload?.data?.size ?? 10),
});

export const getRuleLogs = async (ruleType, params = {}) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return [];
  }

  const specificEndpoint = getRuleEndpoint(ruleType, "logs");
  const isMarginRule = ruleType === "margin";
  const queryParams = isMarginRule
    ? new URLSearchParams({
        page: String(params.page ?? 0),
        size: String(params.size ?? 10),
      }).toString()
    : "";
  const queryParam = !specificEndpoint && ruleType ? `?type=${ruleType}` : "";
  const apiUrl = specificEndpoint
    ? `${localDatabaseUrls.baseURL}${specificEndpoint}${isMarginRule ? `?${queryParams}` : ""}`
    : `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL.ruleLogs}${queryParam}`;

  try {
    const response = await getAPI(apiUrl, tokenInfo);
    if (response?.status === 200) {
      const payload = await response.json().catch(() => ({}));
      return isMarginRule ? normalizeMarginRulePage(payload) : payload;
    }
    const error = await response?.json?.().catch(() => ({}));
    throw new Error(error?.message || "Unable to load rule logs.");
  } catch (error) {
    AlertManager.error(error?.message || "Unable to load rule logs.");
    return [];
  }
};

export const getActiveRuleFlag = async (ruleType) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const endpoint = getRuleEndpoint(ruleType, "active") || ACTIVE_RULE_ENDPOINT;
  if (!endpoint) {
    AlertManager.error("Unable to load active rule configuration.");
    return null;
  }

  const ruleTypeParam = formatRuleType(ruleType);
  const shouldAppendRuleType = RULE_ENDPOINTS[ruleType]?.appendRuleType !== false;
  const ruleTypePath = shouldAppendRuleType && ruleTypeParam ? `/${ruleTypeParam}` : "";
  const apiUrl = `${localDatabaseUrls.baseURL}${endpoint}${ruleTypePath}`;

  try {
    const response = await getAPI(apiUrl, tokenInfo);
    if (response?.status === 200) {
      return response.json();
    }
    const error = await response?.json?.().catch(() => ({}));
    throw new Error(error?.message || "Unable to load active rule configuration.");
  } catch (error) {
    AlertManager.error(error?.message || "Unable to load active rule configuration.");
    return null;
  }
};

export const updateRuleFlag = async (ruleType, payload = {}) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const endpoint = getRuleEndpoint(ruleType, "update") || localDatabaseUrls.serviceURL.ruleUpdate;
  if (!endpoint) {
    AlertManager.error("Unable to update rule configuration.");
    return null;
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${endpoint}`;
  const basePayload = payload && typeof payload === "object" ? payload : {};
  const requestBody = {
    ...basePayload,
    changedBy: basePayload.changedBy || tokenInfo.username,
  };
  const body = new Blob([JSON.stringify(requestBody)], { type: "application/json" });

  try {
    const response = await putAPI(apiUrl, tokenInfo, body);
    if (response?.ok) {
      AlertManager.success("Rule updated successfully.", { contactSupport: false });
      return response;
    }
    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to update rule configuration.");
  } catch (error) {
    AlertManager.error(error?.message || "Unable to update rule configuration.");
    return null;
  }
};

export const getActiveMarginRules = async ({ page = 0, size = 10 } = {}) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const endpoint = localDatabaseUrls.serviceURL.marginStrategySkuStrategies;
  if (!endpoint) {
    AlertManager.error("Unable to load active margin rules.");
    return null;
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${endpoint}?${new URLSearchParams({ page: String(page), size: String(size) }).toString()}`;

  try {
    const response = await getAPI(apiUrl, tokenInfo);
    if (response?.status === 200) {
      const payload = await response.json().catch(() => ({}));
      return normalizeMarginRulePage(payload);
    }
    const error = await response?.json?.().catch(() => ({}));
    throw new Error(error?.message || "Unable to load active margin rules.");
  } catch (error) {
    AlertManager.error(error?.message || "Unable to load active margin rules.");
    return null;
  }
};

export const updateMarginRule = async (payload = {}) => {
  const tokenInfo = await getJWTToken();
  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const endpoint = localDatabaseUrls.serviceURL.marginStrategyUpdate;
  if (!endpoint) {
    AlertManager.error("Unable to update margin rule.");
    return null;
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${endpoint}`;
  const basePayload = payload && typeof payload === "object" ? payload : {};
  const requestBody = {
    ...basePayload,
    changedBy: basePayload.changedBy || tokenInfo.username,
  };
  const body = new Blob([JSON.stringify(requestBody)], { type: "application/json" });

  try {
    const response = await putAPI(apiUrl, tokenInfo, body);
    if (response?.ok) {
      AlertManager.success("Margin rule saved successfully.", { contactSupport: false });
      return response;
    }
    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to save margin rule.");
  } catch (error) {
    AlertManager.error(error?.message || "Unable to save margin rule.");
    return null;
  }
};

export default getRuleLogs;
